import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Button from '../../../components/ui/Button';
import Card, { CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { ballotGuideAPI } from '../../outreach/api/ballot-guide';
import BallotGuideDelivery from '../../outreach/components/BallotGuideDelivery';
import DidYouKnow from '../../outreach/components/DidYouKnow';
import { civicAPI } from '../api/civic';

/**
 * Ballot Results Page - Matched Address
 *
 * Shows personalized voting information organized into 5 key sections:
 * 1. Your Next Election - Date, type, registration deadline
 * 2. Your Ballot - All contests (federal, state, local, school board)
 * 3. Your Polling Place - Location, hours, early voting
 * 4. Your Registration - Status and registration link
 * 5. Your Districts - Federal, state, local district info
 */
export default function BallotResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [guideId, setGuideId] = useState(null);
  const [selections, setSelections] = useState({});

  const address = searchParams.get('address');

  useEffect(() => {
    if (!address) navigate('/', { replace: true });
  }, [address, navigate]);

  const {
    data: voterData,
    isLoading: isLoadingVoter,
    error: voterError,
  } = useQuery({
    queryKey: ['voterInfo', address],
    queryFn: () => civicAPI.getVoterInfo(address),
    enabled: !!address,
  });

  const generateGuide = async () => {
    if (!voterData?.election) return;
    try {
      const result = await ballotGuideAPI.generateGuide(voterData.election.id, address, {
        includeSelections: true,
        includeSummaries: true,
        includePollingInfo: true,
      });
      setGuideId(result.guide.id);
    } catch {
      // Failed to generate guide
    }
  };

  useEffect(() => {
    if (voterData?.election && !guideId) {
      generateGuide();
    }
  }, [voterData, guideId]);

  if (!address) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-neutral-50">
        <Card padding="lg" className="max-w-md">
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">No address provided</h2>
          <p className="text-neutral-600 mb-6">Please search for your address to see your ballot</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Back to Search
          </Button>
        </Card>
      </div>
    );
  }

  if (isLoadingVoter) {
    return (
      <div className="min-h-screen bg-neutral-50 px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-48 bg-neutral-200 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (voterError || !voterData) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-neutral-50">
        <Card padding="lg" className="max-w-md">
          <h2 className="text-2xl font-bold text-error-600 mb-2">⚠️ Address not found</h2>
          <p className="text-neutral-600 mb-2">
            We couldn&apos;t find ballot information for this address.
          </p>
          <p className="text-sm text-neutral-500 mb-6">
            Try: &quot;1234 Congress Ave, Austin, TX 78701&quot;
          </p>
          <Button variant="outline" onClick={() => navigate('/')}>
            Try Another Address
          </Button>
        </Card>
      </div>
    );
  }

  const election = voterData.election;
  const contests = voterData.contests || [];
  const pollingLocations = voterData.pollingLocations || [];
  const districts = voterData.districts || {};

  // Organize contests by type
  const contestsByType = {
    federal: contests.filter((c) => c.contestType === 'federal'),
    state: contests.filter((c) => c.contestType === 'state'),
    local: contests.filter((c) => c.contestType === 'local'),
    schoolBoard: contests.filter((c) => c.contestType === 'school_board'),
    measures: contests.filter((c) => c.contestType === 'measure'),
  };

  const totalRaces = contests.length;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 py-8">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-3xl font-serif font-bold text-neutral-900 mb-2">Your Ballot</h1>
          <p className="text-neutral-600">📍 {address}</p>
        </div>
      </header>

      {/* Main Content - Vertical Stack */}
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* 1. Your Next Election */}
        <Card patrioticShadow>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">📅 Your Next Election</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-neutral-600 font-medium">Election</p>
                <p className="text-lg font-semibold text-neutral-900">{election?.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-600 font-medium">Type</p>
                <p className="text-lg font-semibold text-neutral-900 capitalize">
                  {election?.electionType || 'N/A'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-neutral-200">
              <div>
                <p className="text-sm text-neutral-600 font-medium">Election Day</p>
                <p className="text-lg font-semibold text-neutral-900">
                  {election?.electionDate
                    ? new Date(election.electionDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : election?.electionDay
                      ? new Date(election.electionDay).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-600 font-medium">Register By</p>
                <p className="text-lg font-semibold text-error-600">
                  {election?.registrationDeadline
                    ? new Date(election.registrationDeadline).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })
                    : 'N/A'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-neutral-200">
              <div>
                <p className="text-sm text-neutral-600 font-medium">Early Voting Starts</p>
                <p className="text-lg font-semibold text-emerald-700">
                  {election?.earlyVotingStart
                    ? new Date(election.earlyVotingStart).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-600 font-medium">Absentee Request By</p>
                <p className="text-lg font-semibold text-neutral-900">
                  {election?.absenteeBallotRequestDeadline
                    ? new Date(election.absenteeBallotRequestDeadline).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })
                    : 'N/A'}
                </p>
              </div>
            </div>

            {election?.scheduleWindows?.length > 0 && (
              <div className="pt-4 border-t border-neutral-200 space-y-2">
                <p className="text-sm font-medium text-neutral-700">Early In-Person Voting Hours</p>
                <div className="rounded-lg bg-neutral-50 border border-neutral-200 p-3">
                  <ul className="space-y-1 text-sm text-neutral-700">
                    {election.scheduleWindows.slice(0, 4).map((row) => (
                      <li key={row.label}>• {row.label}</li>
                    ))}
                  </ul>
                  {election.scheduleWindows.length > 4 && (
                    <p className="mt-2 text-xs text-neutral-500">
                      + {election.scheduleWindows.length - 4} more early voting time blocks
                    </p>
                  )}
                </div>
              </div>
            )}

            {election?.registrationDeadline && (
              <div className="pt-4 border-t border-neutral-200">
                <CountdownBadge
                  date={election.registrationDeadline}
                  label="Registration deadline"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* 2. Your Ballot */}
        <Card patrioticShadow>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🗳️ Your Ballot{' '}
              <span className="ml-auto text-base font-normal text-neutral-600">
                {totalRaces} races
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {contests.length === 0 ? (
              <p className="text-neutral-600 text-center py-8">
                No contests found for this address
              </p>
            ) : (
              <div className="space-y-6">
                {/* Federal */}
                {contestsByType.federal.length > 0 && (
                  <ContestGroup
                    title="Federal"
                    contests={contestsByType.federal}
                    selections={selections}
                    onSelect={setSelections}
                  />
                )}

                {/* State */}
                {contestsByType.state.length > 0 && (
                  <ContestGroup
                    title="State"
                    contests={contestsByType.state}
                    selections={selections}
                    onSelect={setSelections}
                  />
                )}

                {/* Local */}
                {contestsByType.local.length > 0 && (
                  <ContestGroup
                    title="Local"
                    contests={contestsByType.local}
                    selections={selections}
                    onSelect={setSelections}
                  />
                )}

                {/* School Board */}
                {contestsByType.schoolBoard.length > 0 && (
                  <ContestGroup
                    title="School Board"
                    contests={contestsByType.schoolBoard}
                    selections={selections}
                    onSelect={setSelections}
                  />
                )}

                {/* Measures */}
                {contestsByType.measures.length > 0 && (
                  <ContestGroup
                    title="Ballot Measures"
                    contests={contestsByType.measures}
                    selections={selections}
                    onSelect={setSelections}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3. Your Polling Place */}
        <Card patrioticShadow>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">📍 Your Polling Place</CardTitle>
          </CardHeader>
          <CardContent>
            {pollingLocations.length === 0 ? (
              <p className="text-neutral-600">No polling locations found for this address</p>
            ) : (
              <div className="space-y-4">
                {pollingLocations.slice(0, 2).map((loc, i) => (
                  <div key={i} className="border border-neutral-200 rounded-lg p-4">
                    <h4 className="font-semibold text-neutral-900 mb-2">{loc.locationName}</h4>
                    <div className="space-y-2 text-sm text-neutral-700 mb-3">
                      <p>📍 {loc.streetAddress}</p>
                      {loc.city && (
                        <p>
                          {loc.city}, {loc.zipCode}
                        </p>
                      )}
                      {loc.hours && <p>🕐 {loc.hours.toString()}</p>}
                    </div>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(
                        `${loc.streetAddress}, ${loc.city}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      Get directions →
                    </a>
                  </div>
                ))}
                {pollingLocations.length > 2 && (
                  <p className="text-sm text-neutral-600 pt-2">
                    +{pollingLocations.length - 2} more polling locations
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 4. Your Registration */}
        <Card patrioticShadow>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">✓ Your Registration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="flex items-center gap-2 font-medium text-emerald-900">
                  <span className="text-lg">✓</span> You're registered to vote
                </p>
                <p className="text-sm text-emerald-800 mt-1">
                  At the address provided for this ballot
                </p>
              </div>
              <p className="text-sm text-neutral-600">
                Not sure about your registration status?
                <a
                  href="https://www.usa.gov/register-to-vote"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 text-primary-600 hover:text-primary-700 font-medium"
                >
                  Check here →
                </a>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 5. Your Districts */}
        <Card patrioticShadow>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">🗺️ Your Districts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DistrictItem label="Congressional District" value={districts.congressional} />
              <DistrictItem label="State Senate" value={districts.stateSenate} />
              <DistrictItem label="State House" value={districts.stateHouse} />
              <DistrictItem label="City Council" value={districts.cityCouncil} />
              <DistrictItem label="School Board" value={districts.schoolBoard} />
              <DistrictItem label="County" value={districts.county} />
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Ballot Guide Delivery CTA */}
      {guideId && (
        <BallotGuideDelivery
          guideId={guideId}
          electionName={election?.name}
          recipientEmail="user@example.com"
        />
      )}
    </div>
  );
}

/**
 * Contest Group - Collapsible section for contest type
 */
function ContestGroup({ title, contests, selections, onSelect }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors text-left font-semibold"
      >
        <span>
          {title} ({contests.length})
        </span>
        <span>{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div className="space-y-3 mt-3 pl-3 border-l-2 border-primary-200">
          {contests.map((contest) => (
            <ContestCard
              key={contest.id}
              contest={contest}
              isSelected={selections[contest.id]}
              onSelect={(candidateId) => {
                onSelect({ ...selections, [contest.id]: candidateId });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Contest Card - Individual race/measure
 */
function ContestCard({ contest, isSelected, onSelect }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4">
      <DidYouKnow office={contest.officeName} district={contest.district} />

      <h3 className="font-semibold text-neutral-900 mt-3 mb-2">
        {contest.officeName || contest.title}
      </h3>

      {contest.district && (
        <p className="text-xs text-neutral-500 mb-3">District: {contest.district}</p>
      )}

      {/* Candidates */}
      {contest.candidates?.length > 0 && (
        <div className="space-y-2 mb-3">
          {contest.candidates.map((candidate) => (
            <label
              key={candidate.id}
              className="flex items-start gap-2 p-2 rounded hover:bg-primary-50 cursor-pointer"
            >
              <input
                type="radio"
                name={contest.id}
                value={candidate.id}
                checked={isSelected === candidate.id}
                onChange={() => onSelect(candidate.id)}
                className="mt-1 shrink-0"
              />
              <div className="flex-1">
                <p className="font-medium text-neutral-900 text-sm">{candidate.name}</p>
                {candidate.party && <p className="text-xs text-neutral-500">{candidate.party}</p>}
              </div>
            </label>
          ))}
        </div>
      )}

      {/* Ballot Measures */}
      {contest.ballotMeasures?.length > 0 && (
        <div className="pt-3 border-t border-neutral-200 space-y-2">
          {contest.ballotMeasures.map((measure) => (
            <div key={measure.id}>
              <p className="font-medium text-sm text-neutral-900">{measure.title}</p>
              <p className="text-xs text-neutral-600 mt-1">{measure.summary}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * District Item
 */
function DistrictItem({ label, value }) {
  return (
    <div className="p-3 bg-neutral-50 rounded-lg">
      <p className="text-xs text-neutral-600 font-medium uppercase">{label}</p>
      <p className="text-sm font-semibold text-neutral-900 mt-1">{value || '—'}</p>
    </div>
  );
}

/**
 * Countdown Badge
 */
function CountdownBadge({ date, label }) {
  const daysUntil = Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div className="flex items-center justify-between p-3 bg-error-50 rounded-lg border border-error-200">
      <div>
        <p className="text-sm font-medium text-error-900">{label}</p>
        <p className="text-xs text-error-700">
          {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </p>
      </div>
      {daysUntil >= 0 && (
        <div
          className={`text-xs font-bold px-2.5 py-1 rounded ${
            daysUntil <= 7 ? 'bg-error-200 text-error-900' : 'bg-warning-200 text-warning-900'
          }`}
        >
          {daysUntil} days
        </div>
      )}
    </div>
  );
}
